import { EventInteraction, useTracking } from '@eventbrite/ads';
import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { GenericLazyString } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import React, { useEffect, useState } from 'react';
import { EventSearchInterfaceName } from '../../../../api/searchAllEvents';
import { DiscoverVerticalEventCard } from '../../../../components/DiscoverEventCard/DiscoverVerticalEventCard';
import TOP_10_CITIES from '../../../../__fixtures__/top10Cities.json';
import { getTrendingTagEvents } from '../../api/trendingTagEvents';
import { CategoryBrowseBucket } from '../../components/CategoryBrowseBucket';
import { EventLoadingState } from '../../components/CategoryBrowseEventsLoading';
import { categoryMappings } from '../../constants/trendingTagEvents';
import { useEnvContext, useRouteContext } from '../../context';
import { aggregateContext } from '../../utils/analytics';
import CategoryBrowseOrganizerTags from '../CategoryBrowseOrganizerTags';
import './TrendingTagEvents.scss';

const categoryBrowseUtmOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-share-source': 'category-browse',
    'utm-term': '',
};

const EventCardSlide: React.FC = ({ children }) => {
    return <div className="event-card-simple-carousel-slide">{children}</div>;
};

interface TrendingTagEventsProps {
    isNightlife?: boolean;
    title: string | GenericLazyString;
    lazyLoadCarouselElements?: boolean;
    bucketName?: EventSearchInterfaceName;
    bucketKey: string;
    bucketPosition?: string;
    affCode?: string;
    placeId: string;
}
interface TrendingTagEventsData {
    name: string;
    events: FormattedEvent[];
    tagline: string;
}

export const TrendingTagEvents: React.FC<TrendingTagEventsProps> = ({
    isNightlife = false,
    title,
    bucketKey,
    bucketPosition,
    lazyLoadCarouselElements,
    bucketName = 'home_category',
    affCode,
    placeId,
}) => {
    const { user, env, request } = useEnvContext();
    const { parsedValues } = useRouteContext();
    const seasonalBucket = parsedValues?.bucket;
    const isAuthenticated = !!user?.isAuthenticated;
    const locale = env?.localeInfo.locale || 'en_US';
    const category = categoryMappings[parsedValues?.activeTag || ''];
    const isTopCity = Object.hasOwnProperty.call(TOP_10_CITIES, placeId);

    const showTrendingEventsModule = isTopCity && category !== undefined;

    const [data, setData] = useState<TrendingTagEventsData[]>([]);
    const [isFetching, setIsFetching] = useState<boolean>(
        showTrendingEventsModule,
    );

    useEffect(() => {
        // Fetch events for the top 3 trending tags
        const fetchEvents = async () => {
            let response =
                (await getTrendingTagEvents(category, placeId)) ?? [];
            if (category === 'holidays' || category === 'nye')
                response = response.slice(0, 2);
            setData(response || []);
            setIsFetching(false);
        };
        if (showTrendingEventsModule) {
            fetchEvents();
        } else {
            setIsFetching(false);
        }
    }, [category, placeId, showTrendingEventsModule]);

    const events: FormattedEvent[] = data
        ? data.map((bucket) => bucket.events).flat()
        : [];

    const getTrackingContext = ({ id, event }: EventInteraction) =>
        aggregateContext({
            id,
            event,
            bucketKey,
            bucketPosition,
            events,
            eventSearch: {},
            userId: user?.publicId,
            locale,
            sessionId: request?.session_id,
            guestId: user?.guestId,
            bucketName,
        });

    const { ref } = useTracking<HTMLDivElement>(getTrackingContext);
    const bucketLabel = title?.toString();
    const affiliateCode =
        seasonalBucket && affCode ? affCode : 'ebdssbcategorybrowse';

    const getCommonEventCardProps = (event: FormattedEvent) => ({
        locale,
        statsigLocationString: 'CategoryBrowse - AI Module',
        isAuthenticated,
        event,
        isLazyImage: true,
        onClick: handleOnClick,
        affCode: affiliateCode,
        bucketLabel,
        shareOptions: { utmOptions: categoryBrowseUtmOptions },
    });

    const handleOnClick = React.useCallback(() => {
        trackAnalyticsEvent({
            category: 'category-page',
            action: 'ViewEvent',
            label: title.toString(),
        });
    }, [title]);

    const loading = isFetching;

    //  Display a loading state if the data is still being fetched
    if (loading) {
        return <EventLoadingState />;
    }

    // If the user is in the control group or there are no events to show, render the organizer tags
    if ((!isFetching && data.length === 0) || !showTrendingEventsModule) {
        return <CategoryBrowseOrganizerTags />;
    }

    if (typeof window === 'undefined') return null;

    const handleNavigationClick = () => {
        logEvent('CategoryBrowse - AI Module - Click - Arrow');
    };

    // Show side navigation if the window width is greater than 1080px
    const showSideNavigation = () => {
        return typeof window !== 'undefined' && window.innerWidth > 1080;
    };

    return (
        <div
            data-event-bucket-label={bucketKey?.toString()}
            ref={ref}
            className={`trending-tag-events__container ${
                isNightlife && 'nightlife'
            }`}
        >
            {data.map((bucket: any, index: number) => (
                <CategoryBrowseBucket key={index}>
                    <SimpleCarousel
                        slidesVisible={{ desktop: 3, tablet: 2 }}
                        headerTitle={bucket.name}
                        subtitle={bucket.tagline}
                        lazilyRenderOffScreenElements={lazyLoadCarouselElements}
                        useSideNavigation={
                            showSideNavigation() && bucket.events.length > 3
                        }
                        onNavigationClick={handleNavigationClick}
                    >
                        {bucket.events?.map((event: any) => (
                            <EventCardSlide key={event.id}>
                                <DiscoverVerticalEventCard
                                    {...getCommonEventCardProps(event)}
                                    showOrganizer={false}
                                    data-testid="category-browse-organic-event"
                                />
                            </EventCardSlide>
                        ))}
                    </SimpleCarousel>
                </CategoryBrowseBucket>
            ))}
        </div>
    );
};
